import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { productMatcher } from '@app/+product/product-routing.module';
import {
  CatalogMatcher,
  searchPathMatcher,
  externalLinkLoggedMatcher,
  campaignMatcher,
  directShippingMatcher,
  expressDeliveryMatcher,
  defaultCatalogPageAnalytics,
  catalogPageType
} from '@app/+catalog/catalog-page-routing.module';
import { ExternalLinkResolver } from '@app/shared/guards/externalLink-guard.service';
import { HomeComponent } from './home/home.component';
import { CatalogPageComponent } from '@app/+catalog/catalog-page.component';
import { CatalogPageResolver } from '@app/+catalog/catalog-page-resolver.service';
import { BreadcrumbResolver } from '@app/shared/resolvers/breadcrumb-resolver.service';
import { VoucherGuard, AuthenticateExternalLinkGuard, IsLoggedGuard } from '@app/shared/guards';
import { CatalogLinksResolver } from '@app/+catalog/catalog-links-resolver.service';
import { landingRoutes } from '@app/landings/landing-routes';
import { CatalogCampaignResolver } from './+catalog/catalog-campaign-resolver.service';
import { cmsPageLandingUrlMatcher } from './+landing-cms/cms-landing-routing.module';
import { PromotionsLegalConditionsComponent } from '@app/core/promo-banner/promotions-legal-conditions/promotions-legal-conditions.component';
import { CmsResolver } from '@app/library/resolvers/cms-resolver.service';
import { notFoundMatcher } from './core/not-found/not-found.module';
import { affiliationMatcher } from './+affiliation/affiliation-page-routing.module';
import { QuicklinkStrategy, QuicklinkModule } from 'ngx-quicklink';
import { ChatGuard } from './+chat/guard/chat.guard';
import { DirectShippingEligibilityGuard } from '@app/user/direct-shipping-eligibility/guard/direct-shipping-eligibility.guard';
import { journalMainUrlMatcher, JournalMainRoutingModule } from './+journal-main/journal-main-routing-module';
import { cmsPageEditoUrlMatcher } from './+edito-cms/cms-edito-routing.module';
import { chatPathMatcher } from './+chat/chat-routing.module';
import { FeedGuard } from './+feed/guard/feed.guard';
import { feedPathMatcher } from './+feed/feed-routing.module';
import { GetPaidGuard } from './+payment/guard/get-paid.guard';
import { getPaidPathMatcher } from './+payment/seller-payment-page-routing.module';
import { JournalMainResolver } from './+journal-main/resolvers/journal-main.resolver';
import { JournalMainGuard } from './+journal-main/journal-main.guard';

export const routes: Routes = [
  /**
   * Chat
   */
  {
    matcher: chatPathMatcher,
    loadChildren: () => import('./+chat/chat.module').then(m => m.ChatModule),
    canActivate: [ChatGuard],
    data: { pageAnalytics: { env_template: 'bs_chat', pageUrl: '/chat/messages' } }
  },
  /**
   * Feed
   */
  {
    matcher: feedPathMatcher,
    loadChildren: () => import('./+feed/feed.module').then(m => m.FeedModule),
    canActivate: [FeedGuard]
  },
  /**
   * Home
   */
  {
    path: '',
    component: HomeComponent,
    canActivate: [VoucherGuard],
    resolve: {
      CMS: CmsResolver
    },
    canDeactivate: [IsLoggedGuard],
    data: { pageAnalytics: { env_template: 'index_home', pageUrl: '/homePage' } }
  },
  /**
   * Landing CMS
   */
  {
    matcher: cmsPageLandingUrlMatcher,
    loadChildren: () => import('./+landing-cms/cms-landing.module').then(m => m.CmsLandingModule),
    canActivate: [VoucherGuard],
    canDeactivate: [IsLoggedGuard],
    resolve: {
      CMS: CmsResolver
    },
    data: { pageAnalytics: { env_template: 'channels', pageUrl: '/l' } }
  },
  /**
   * Journal Main Page CMS
   */
  {
    matcher: journalMainUrlMatcher,
    loadChildren: () => import('./+journal-main/journal-main.module').then(m => m.JournalMainModule),
    resolve: {
      pageData: JournalMainResolver
    },
    canActivate: [JournalMainGuard],
    data: { pageAnalytics: { env_template: 'journal-main', pageUrl: '/journal/' } }
  },
  /**
   * Edito CMS
   */
  {
    matcher: cmsPageEditoUrlMatcher,
    loadChildren: () => import('./+edito-cms/cms-edito.module').then(m => m.CmsEditoModule),
    canActivate: [VoucherGuard],
    canDeactivate: [IsLoggedGuard],
    resolve: {
      CMS: CmsResolver
    },
    data: { pageAnalytics: { env_template: 'edito', pageUrl: '/journal' } }
  },
  /**
   * Search
   */
  {
    matcher: searchPathMatcher,
    component: CatalogPageComponent,
    canDeactivate: [IsLoggedGuard],
    data: { pageAnalytics: { env_template: 'catalog', pageUrl: '/search/productList' } }
  },
  {
    path: 's/fashion/:query',
    component: CatalogPageComponent,
    canDeactivate: [IsLoggedGuard],
    data: { pageAnalytics: { env_template: 'catalog', pageUrl: '/search/productList' } }
  },
  /**
   * Affiliation page
   */
  {
    matcher: affiliationMatcher,
    loadChildren: () => import('./+affiliation/affiliation-page.module').then(m => m.AffiliationPageModule)
  },
  /**
   * Payment
   */
  {
    matcher: getPaidPathMatcher,
    loadChildren: () => import('./+payment/seller-payment-page.module').then(m => m.SellerPaymentPageModule),
    canActivate: [GetPaidGuard]
  },
  /**
   * Promotion Condition
   */
  {
    path: 'promotions-legal-conditions/',
    component: PromotionsLegalConditionsComponent,
    data: {
      pageAnalytics: {
        list_products: []
      }
    }
  },
  /**
   * Direct shipping eligibility page
   */
  {
    path: 'profile/direct-shipping/conditions/',
    loadChildren: () =>
      import('./user/direct-shipping-eligibility/direct-shipping-eligibility.module').then(
        m => m.DirectShippingEligibilityModule
      ),
    canActivate: [DirectShippingEligibilityGuard]
  },
  /**
   * Landing Pages
   */
  ...landingRoutes,
  /**
   * Product
   */ {
    matcher: productMatcher,
    loadChildren: () => import('./+product/product.module').then(m => m.ProductModule),
    data: {
      pageAnalytics: { env_template: 'product', list_products: [] },
      routename: 'product'
    }
  },
  /**
   * Route vers legacy connecté
   */
  {
    matcher: externalLinkLoggedMatcher,
    component: CatalogPageComponent,
    canActivate: [IsLoggedGuard, AuthenticateExternalLinkGuard]
  },
  /**
   * Campaign router
   */
  {
    matcher: campaignMatcher,
    component: CatalogPageComponent,
    canDeactivate: [IsLoggedGuard],
    resolve: { campaign: CatalogCampaignResolver, catalogLinks: CatalogLinksResolver },
    data: { pageAnalytics: defaultCatalogPageAnalytics, pageType: catalogPageType.campaign }
  },
  /**
   * Not Found
   */
  {
    matcher: notFoundMatcher,
    loadChildren: () => import('./core/not-found/not-found.module').then(m => m.NotFoundModule),
    data: { pageAnalytics: { env_template: 'default_noside', list_products: [] } }
  },
  {
    matcher: directShippingMatcher,
    component: CatalogPageComponent,
    canDeactivate: [IsLoggedGuard],
    resolve: {
      pageData: CatalogPageResolver,
      breadcrumb: BreadcrumbResolver,
      catalogLinks: CatalogLinksResolver
    },
    data: { pageAnalytics: defaultCatalogPageAnalytics, pageType: catalogPageType.directShipping }
  },
  {
    matcher: expressDeliveryMatcher,
    component: CatalogPageComponent,
    canDeactivate: [IsLoggedGuard],
    resolve: {
      pageData: CatalogPageResolver,
      breadcrumb: BreadcrumbResolver,
      catalogLinks: CatalogLinksResolver
    },
    data: { pageAnalytics: defaultCatalogPageAnalytics, pageType: catalogPageType.expressDelivery }
  },
  /**
   * Catalog + legacy non connecté
   */
  {
    matcher: CatalogMatcher,
    component: CatalogPageComponent,
    canDeactivate: [IsLoggedGuard],
    resolve: {
      pageData: CatalogPageResolver,
      breadcrumb: BreadcrumbResolver,
      catalogLinks: CatalogLinksResolver
    },
    data: { pageAnalytics: defaultCatalogPageAnalytics, pageType: catalogPageType.default }
  },
  /**
   * Not Found
   */ {
    path: '**',
    loadChildren: () => import('./core/not-found/not-found.module').then(m => m.NotFoundModule),
    canActivate: [ExternalLinkResolver],
    data: { pageAnalytics: { env_template: 'default_noside', list_products: [] } }
  }
];

@NgModule({
  imports: [
    QuicklinkModule,
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabled',
      scrollPositionRestoration: 'enabled',
      preloadingStrategy: QuicklinkStrategy,
      relativeLinkResolution: 'legacy'
    })
  ],
  exports: [RouterModule],
  providers: [BreadcrumbResolver]
})
export class AppRoutingModule {}
